import type { ReactNode } from 'react';
import { DetailHeader } from './DetailHeader';
import type { ContainerKind } from '../components';
import './operation-detail.css';

/**
 * Page scaffold for every operation-detail view in the admin console — hint,
 * submission, model run, playground result, text-chat detail, concept-lab
 * detail, etc.
 *
 * Enforces section order and consistent outer chrome. Callers pass each
 * section as a slot prop; the scaffold places them in the canonical order:
 *
 *   Header → Context → Input → Execution → Outcome → (children)
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface OperationDetailPageProps {
    title: string;
    id: string;
    idContainer?: ContainerKind;
    backLabel: string;
    onBack: () => void;
    titleRight?: ReactNode;
    actions?: ReactNode;
    actionStatus?: ReactNode;

    /** Walking the list this detail was opened from — the Prev/Next strip and
     *  its gutter arrows (see `@shared-ui/item-nav`).
     *
     *  Below the header on purpose: leaving the list and stepping within it are
     *  different kinds of move, and the way out should be the first thing read.
     *  It also has to render inside this page's column, which is what lets the
     *  arrows find the gutter. */
    navSlot?: ReactNode;

    /** Context row — two cards (user + metadata). Passed as a single node so
     *  callers can use `<ContextGrid>` or a plain one-column layout. */
    context?: ReactNode;

    /** Input section — question + student work cards. */
    input?: ReactNode;

    /** Optional operation-level summary rendered between `input` and `runs`.
     *  Use for content that describes the OPERATION as a whole rather than
     *  any single run — e.g. the "Hint Generated" panel on Hint Detail.
     *  Anything below this slot (the runs table + per-run detail) is
     *  scoped to the currently-selected run. */
    operationSummary?: ReactNode;

    /** Runs table (1+ rows). */
    runs?: ReactNode;

    /** Selected-run drill-down card. */
    runDetail?: ReactNode;

    /** Operation-level error card (rendered last when present). */
    errorSection?: ReactNode;

    /** Extra slots after runDetail but before errorSection — e.g. action
     *  modules that live outside the run-detail card. */
    children?: ReactNode;
}

export function OperationDetailPage({
    title,
    id,
    idContainer,
    backLabel,
    onBack,
    titleRight,
    actions,
    actionStatus,
    navSlot,
    context,
    input,
    operationSummary,
    runs,
    runDetail,
    errorSection,
    children,
}: OperationDetailPageProps) {
    return (
        <div className="operation-detail-page">
            <DetailHeader
                title={title}
                id={id}
                idContainer={idContainer}
                backLabel={backLabel}
                onBack={onBack}
                titleRight={titleRight}
                actions={actions}
                actionStatus={actionStatus}
            />
            {navSlot}
            {context}
            {input}
            {operationSummary}
            {runs}
            {runDetail}
            {children}
            {errorSection}
        </div>
    );
}
